<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import AutomationAPI from 'dashboard/api/automation';
import SidePanel from 'dashboard/components-next/side-panel/SidePanel.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  EXECUTIONS_PER_PAGE,
  EXECUTION_STATUS_FILTERS,
  RUNS_PER_PAGE,
  RUN_ALERT_STATUSES,
  RUN_TRIGGER_MANUAL,
  apiErrorMessage,
  formatCalendarDate,
  runErrorCode,
} from '../dateTrigger';

const { t, locale } = useI18n();

const panelRef = ref(null);
const rule = ref(null);
const runs = ref([]);
const runsPage = ref(1);
const runsTotal = ref(0);
const isLoadingRuns = ref(false);
const errorMessage = ref('');

const selectedRunId = ref(null);
const executionFilter = ref(EXECUTION_STATUS_FILTERS[0]);
const executions = ref([]);
const executionsPage = ref(1);
const executionsTotal = ref(0);
const isLoadingExecutions = ref(false);

const title = computed(() =>
  rule.value
    ? t('AUTOMATION.DATE_TRIGGER.HISTORY.TITLE', { name: rule.value.name })
    : ''
);

const formatDate = isoDate => formatCalendarDate(isoDate, locale.value);

const showError = error => {
  errorMessage.value =
    apiErrorMessage(error) || t('AUTOMATION.DATE_TRIGGER.HISTORY.ERROR');
};

const fetchRuns = async page => {
  isLoadingRuns.value = true;
  errorMessage.value = '';
  try {
    const { data } = await AutomationAPI.getDateTriggerRuns(
      rule.value.id,
      page
    );
    runs.value = data.payload;
    runsTotal.value = data.meta.total_count;
    runsPage.value = page;
  } catch (error) {
    showError(error);
  } finally {
    isLoadingRuns.value = false;
  }
};

const fetchExecutions = async page => {
  isLoadingExecutions.value = true;
  errorMessage.value = '';
  try {
    const { data } = await AutomationAPI.getDateTriggerExecutions(
      rule.value.id,
      selectedRunId.value,
      { status: executionFilter.value, page }
    );
    executions.value = data.payload;
    executionsTotal.value = data.meta.total_count;
    executionsPage.value = page;
  } catch (error) {
    showError(error);
  } finally {
    isLoadingExecutions.value = false;
  }
};

const toggleRun = run => {
  if (selectedRunId.value === run.id) {
    selectedRunId.value = null;
    return;
  }
  selectedRunId.value = run.id;
  executionFilter.value = EXECUTION_STATUS_FILTERS[0];
  executions.value = [];
  fetchExecutions(1);
};

const selectFilter = filter => {
  executionFilter.value = filter;
  fetchExecutions(1);
};

const triggerLabel = run => {
  if (run.trigger !== RUN_TRIGGER_MANUAL) {
    return t('AUTOMATION.DATE_TRIGGER.HISTORY.TRIGGERS.SCHEDULED');
  }
  return run.triggered_by
    ? t('AUTOMATION.DATE_TRIGGER.HISTORY.TRIGGERS.MANUAL_BY', {
        name: run.triggered_by.name,
      })
    : t('AUTOMATION.DATE_TRIGGER.HISTORY.TRIGGERS.MANUAL');
};

const statusLabel = run =>
  t(`AUTOMATION.DATE_TRIGGER.HISTORY.STATUSES.${run.status.toUpperCase()}`);

const runAlert = run =>
  RUN_ALERT_STATUSES.includes(run.status)
    ? t(
        `AUTOMATION.DATE_TRIGGER.LAST_RUN.ERRORS.${runErrorCode(run).toUpperCase()}`
      )
    : null;

// Data ilegível não é registro pulado: o backend guarda a contagem à parte em skipped_counts.invalid_date.
const invalidDateCount = run => run.skipped_counts?.invalid_date || 0;

const skippedTotal = run =>
  Object.values(run.skipped_counts || {}).reduce(
    (total, count) => total + count,
    0
  ) - invalidDateCount(run);

const countsLabel = run =>
  t('AUTOMATION.DATE_TRIGGER.HISTORY.COUNTS', {
    matched: run.matched_count,
    executed: run.executed_count,
    skipped: skippedTotal(run),
    failed: run.failed_count,
  });

const executionStatusLabel = execution =>
  execution.skip_reason
    ? t(
        `AUTOMATION.DATE_TRIGGER.SKIP_REASONS.${execution.skip_reason.toUpperCase()}`
      )
    : t(
        `AUTOMATION.DATE_TRIGGER.HISTORY.EXECUTION_STATUSES.${execution.status.toUpperCase()}`
      );

const open = automation => {
  rule.value = automation;
  runs.value = [];
  runsTotal.value = 0;
  selectedRunId.value = null;
  panelRef.value?.open();
  fetchRuns(1);
};

defineExpose({ open });
</script>

<template>
  <SidePanel ref="panelRef" width="2xl" :title="title">
    <div class="flex flex-col w-full gap-3">
      <p
        v-if="errorMessage"
        data-test="history-error"
        class="mb-0 text-sm text-n-ruby-11"
      >
        {{ errorMessage }}
      </p>
      <Spinner v-if="isLoadingRuns" :size="20" class="text-n-slate-10" />
      <p
        v-else-if="!runs.length"
        data-test="history-empty"
        class="mb-0 text-sm text-n-slate-11"
      >
        {{ $t('AUTOMATION.DATE_TRIGGER.HISTORY.EMPTY') }}
      </p>
      <ul v-else class="flex flex-col gap-2 p-0 m-0 list-none">
        <li
          v-for="run in runs"
          :key="run.id"
          data-test="history-run"
          class="flex flex-col gap-2 p-3 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak"
        >
          <button
            type="button"
            data-test="history-run-toggle"
            class="flex flex-wrap items-center w-full gap-x-3 gap-y-1 text-start"
            @click="toggleRun(run)"
          >
            <span class="text-sm font-medium text-n-slate-12">
              {{ formatDate(run.run_on) }}
            </span>
            <span class="text-xs text-n-slate-11">{{ triggerLabel(run) }}</span>
            <span
              v-if="runAlert(run)"
              class="flex items-center gap-1 text-xs text-n-ruby-11"
            >
              <Icon icon="i-lucide-triangle-alert" class="size-3.5" />
              {{ runAlert(run) }}
            </span>
            <span v-else class="text-xs text-n-slate-11">
              {{ statusLabel(run) }}
            </span>
            <span class="text-xs text-n-slate-10">{{ countsLabel(run) }}</span>
            <span
              v-if="invalidDateCount(run)"
              data-test="history-invalid-dates"
              class="text-xs text-n-amber-11"
            >
              {{
                $t(
                  'AUTOMATION.DATE_TRIGGER.HISTORY.INVALID_DATES',
                  { count: invalidDateCount(run) },
                  invalidDateCount(run)
                )
              }}
            </span>
          </button>

          <div
            v-if="selectedRunId === run.id"
            data-test="history-executions"
            class="flex flex-col gap-2 pt-2 border-t border-n-weak"
          >
            <div class="flex gap-2">
              <NextButton
                v-for="filter in EXECUTION_STATUS_FILTERS"
                :key="filter"
                :data-test="`history-filter-${filter}`"
                :label="
                  $t(
                    `AUTOMATION.DATE_TRIGGER.HISTORY.FILTERS.${filter.toUpperCase()}`
                  )
                "
                :color="executionFilter === filter ? 'blue' : 'slate'"
                faded
                xs
                @click="selectFilter(filter)"
              />
            </div>
            <Spinner
              v-if="isLoadingExecutions"
              :size="16"
              class="text-n-slate-10"
            />
            <p
              v-else-if="!executions.length"
              class="mb-0 text-xs text-n-slate-11"
            >
              {{ $t('AUTOMATION.DATE_TRIGGER.HISTORY.EXECUTIONS_EMPTY') }}
            </p>
            <ul v-else class="flex flex-col gap-1 p-0 m-0 list-none">
              <li
                v-for="execution in executions"
                :key="execution.id"
                data-test="history-execution"
                class="flex flex-wrap items-center gap-x-3 text-xs"
              >
                <span class="text-n-slate-12">{{ execution.label }}</span>
                <span class="text-n-slate-10">
                  {{ formatDate(execution.occurrence_date) }}
                </span>
                <span class="text-n-slate-11">
                  {{ executionStatusLabel(execution) }}
                </span>
                <span v-if="execution.details?.error" class="text-n-ruby-11">
                  {{ execution.details.error }}
                </span>
              </li>
            </ul>
            <PaginationFooter
              v-if="executionsTotal > EXECUTIONS_PER_PAGE"
              :current-page="executionsPage"
              :total-items="executionsTotal"
              :items-per-page="EXECUTIONS_PER_PAGE"
              @update:current-page="fetchExecutions"
            />
          </div>
        </li>
      </ul>
      <PaginationFooter
        v-if="runsTotal > RUNS_PER_PAGE"
        :current-page="runsPage"
        :total-items="runsTotal"
        :items-per-page="RUNS_PER_PAGE"
        @update:current-page="fetchRuns"
      />
    </div>
  </SidePanel>
</template>
